import { useCreate, useInvalidate } from '@refinedev/core'
import { DatePicker, Form, Input, InputNumber, Modal, Switch } from 'antd'
import { useState } from 'react'

interface Props {
  open: boolean
  onClose: () => void
}

export function CreatePromoCodeModal({ open, onClose }: Props) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const { mutate: create } = useCreate()
  const invalidate = useInvalidate()

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const body = {
        code: values.code,
        amount: String(values.amount),
        maxTotalUsages: values.maxTotalUsages,
        freeCreditsDurationDays: values.freeCreditsDurationDays ?? 0,
        isActive: values.isActive ?? true,
        validFrom: values.validFrom?.toISOString() ?? null,
        validTo: values.validTo.toISOString(),
      }

      create(
        { resource: 'promo-codes', values: body },
        {
          onSuccess: () => {
            form.resetFields()
            onClose()
            void invalidate({ resource: 'promo-codes', invalidates: ['list'] })
          },
          onSettled: () => setLoading(false),
        },
      )
    } catch {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Create Promo Code"
      open={open}
      onOk={() => void handleSubmit()}
      okText="Create Promo Code"
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnHidden
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ isActive: true }}
      >
        <Form.Item
          name="code"
          label="Code"
          rules={[{ required: true, message: 'Code is required' }]}
        >
          <Input placeholder="e.g. WELCOME50" />
        </Form.Item>

        <Form.Item
          name="amount"
          label="Amount ($)"
          rules={[{ required: true, message: 'Amount is required' }]}
        >
          <InputNumber
            min={0}
            step={0.01}
            style={{ width: '100%' }}
            placeholder="10.00"
          />
        </Form.Item>

        <Form.Item
          name="maxTotalUsages"
          label="Max Total Usages"
          rules={[{ required: true }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} placeholder="100" />
        </Form.Item>

        <Form.Item
          name="freeCreditsDurationDays"
          label="Free Credits Duration (days)"
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="30" />
        </Form.Item>

        <Form.Item name="validFrom" label="Valid From">
          <DatePicker style={{ width: '100%' }} showTime />
        </Form.Item>

        <Form.Item name="validTo" label="Valid To" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} showTime />
        </Form.Item>

        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
